import React, { useCallback, useEffect, useState } from "react";
import logo from "./assets/logo.png";
import { clearStaffSession, getStaffToken, staffApiFetch } from "./staffApi";

export default function StaffWorkspace() {
  const [leads, setLeads] = useState([]);

  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [noteType, setNoteType] = useState("General");
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState("");
  const [actionSuccess, setActionSuccess] = useState("");

  const selectLead = (lead) => {
    setSelectedId(lead._id);
    setEdit({
      name: lead.name || "",
      requirement: lead.requirement || "",
      status: ["New", "In Progress", "Won", "Lost"].includes(lead.status)
        ? lead.status : "New",
      next_followup_date: lead.next_followup_date || ""
    });
    setNoteText("");
    setNoteType("General");
    setActionError("");
    setActionSuccess("");
  };

  const updateLocalLead = (updatedLead) => {
    setLeads(previous => previous.map(lead =>
      lead._id === updatedLead._id ? updatedLead : lead
    ));
  };

  const saveChanges = async () => {
    if (!selectedId || !edit || saving) return;

    setSaving(true);
    setActionError("");
    setActionSuccess("");

    try {
      const response = await staffApiFetch(`/staff-crm/lead/${selectedId}`, {
        method: "PUT",
        body: JSON.stringify(edit)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Unable to save changes.");
      }

      const updatedLead = data.lead || data;
      if (!updatedLead._id) {
        throw new Error("Unexpected update response.");
      }

      updateLocalLead(updatedLead);
      selectLead(updatedLead);
      setActionSuccess("Customer changes saved.");
    } catch (err) {
      setActionError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const addNote = async () => {
    if (!selectedId || !noteText.trim() || saving) return;

    setSaving(true);
    setActionError("");
    setActionSuccess("");

    try {
      const response = await staffApiFetch(`/staff-crm/lead/${selectedId}/note`, {
        method: "POST",
        body: JSON.stringify({ note: noteText.trim(), type: noteType })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Unable to add note.");
      }

      const updatedLead = data.lead || data;
      if (!updatedLead._id) {
        throw new Error("Unexpected note response.");
      }

      updateLocalLead(updatedLead);
      setNoteText("");
      setActionSuccess("Note added.");
    } catch (err) {
      setActionError(err.message);
    } finally {
      setSaving(false);
    }
  };


  const loadData = useCallback(async () => {
    try {
      const leadsResponse = await staffApiFetch("/staff-crm/leads");

      if (leadsResponse.status === 401) {
        window.location.replace("/staff-login");
        return;
      }

      if (!leadsResponse.ok) {
        throw new Error(
          leadsResponse.status === 403
            ? "Your CRM access is currently unavailable. Please contact the CRM owner."
            : "Unable to load your assigned CRM."
        );
      }

      const leadsData = await leadsResponse.json();
      setLeads(Array.isArray(leadsData) ? leadsData : leadsData.leads || []);
      setError("");
    } catch (err) {
      setError(err.message || "Unable to load CRM.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!getStaffToken()) {
      window.location.replace("/staff-login");
      return;
    }
    loadData();
  }, [loadData]);

  const logout = async () => {
    try {
      await staffApiFetch("/staff/logout", { method: "POST" });
    } catch {
      // Clear the local session even when the network is unavailable.
    } finally {
      clearStaffSession();
      window.location.replace("/staff-login");
    }
  };

  const filteredLeads = leads.filter(lead => {
    const value = `${lead.name || ""} ${lead.phone || ""} ${lead.requirement || ""}`;
    return value.toLowerCase().includes(search.toLowerCase());
  });

  const selectedLead = leads.find(lead => lead._id === selectedId);

  const panel = {
    background: "white",
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    padding: 20,
    marginBottom: 18
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f5f7fb" }}>
      <header style={{
        background: "#2c3e50",
        color: "white",
        padding: "12px 24px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 12
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <img src={logo} alt="MUKBI" style={{
            height: 40, background: "white", borderRadius: 6, padding: 4
          }} />
          <div>
            <strong>MUKBI Staff Workspace</strong>
            <div style={{ fontSize: 12, color: "#cbd5e1" }}>
              {localStorage.getItem("mukbiStaffName") || "Staff"}
            </div>
          </div>
        </div>
        <button onClick={logout} style={{
          background: "transparent", color: "white",
          border: "1px solid #94a3b8", borderRadius: 6,
          padding: "8px 14px", cursor: "pointer"
        }}>Logout</button>
      </header>

      <main style={{ maxWidth: 1150, margin: "auto", padding: 24 }}>
        <h2>Customer Workspace</h2>
        <p style={{ color: "#64748b" }}>
          View customers, follow-ups and conversation history.
        </p>

        {error && (
          <div role="alert" style={{ ...panel, color: "#b91c1c" }}>
            {error}
          </div>
        )}

        {loading ? (
          <p>Loading your CRM...</p>
        ) : !error && (
          <>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              gap: 12,
              marginBottom: 18
            }}>
              {[
                ["Total Customers", leads.length],
                ["New", leads.filter(l => l.status === "New").length],
                ["In Progress", leads.filter(l => l.status === "In Progress").length],
                ["Won", leads.filter(l => l.status === "Won").length],
                ["Lost", leads.filter(l => l.status === "Lost").length]
              ].map(([label, value]) => (
                <div key={label} style={panel}>
                  <div style={{ color: "#64748b", fontSize: 13 }}>{label}</div>
                  <div style={{ fontSize: 28, fontWeight: 700 }}>{value}</div>
                </div>
              ))}
            </div>

            <div style={panel}>
              <h3>Customers</h3>
              <input
                aria-label="Search customers"
                placeholder="Search name, phone or requirement"
                value={search}
                onChange={event => setSearch(event.target.value)}
                style={{
                  width: "100%", maxWidth: 450, boxSizing: "border-box",
                  padding: 12, border: "1px solid #cbd5e1", borderRadius: 8
                }}
              />

              <div style={{ marginTop: 16 }}>
                {filteredLeads.length === 0 && <p>No customers found.</p>}
                {filteredLeads.map(lead => (
                  <button key={lead._id}
                    onClick={() => selectLead(lead)}
                    style={{
                      display: "block", width: "100%", textAlign: "left",
                      padding: 14, marginBottom: 8, borderRadius: 8,
                      border: selectedId === lead._id
                        ? "2px solid #2563eb" : "1px solid #e2e8f0",
                      background: "#fff", cursor: "pointer"
                    }}>
                    <strong>{lead.name || "Unnamed Customer"}</strong>
                    <div style={{ fontSize: 13, color: "#64748b" }}>
                      {lead.phone || "No phone"} · {lead.status || "New"}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {selectedLead && (
              <div style={panel}>
                <h3>Customer Details</h3>

                {actionError && (
                  <p role="alert" style={{ color: "#b91c1c" }}>{actionError}</p>
                )}
                {actionSuccess && (
                  <p role="status" style={{ color: "#15803d" }}>{actionSuccess}</p>
                )}

                <p><strong>Phone (read-only):</strong> {selectedLead.phone || "—"}</p>

                {edit && (
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: 14
                  }}>
                    <label>
                      Customer Name
                      <input
                        value={edit.name}
                        onChange={e => setEdit({
                          ...edit, name: e.target.value
                        })}
                        style={{ display: "block", width: "95%", padding: 9 }}
                      />
                    </label>

                    <label>
                      Requirement
                      <input
                        value={edit.requirement}
                        onChange={e => setEdit({
                          ...edit, requirement: e.target.value
                        })}
                        style={{ display: "block", width: "95%", padding: 9 }}
                      />
                    </label>

                    <label>
                      Status
                      <select
                        value={edit.status}
                        onChange={e => setEdit({
                          ...edit, status: e.target.value
                        })}
                        style={{ display: "block", width: "100%", padding: 9 }}
                      >
                        {["New", "In Progress", "Won", "Lost"].map(status => (
                          <option key={status} value={status}>{status}</option>
                        ))}
                      </select>
                    </label>

                    <label>
                      Next Follow-up Date
                      <input
                        type="date"
                        value={edit.next_followup_date}
                        onChange={e => setEdit({
                          ...edit, next_followup_date: e.target.value
                        })}
                        style={{ display: "block", width: "95%", padding: 9 }}
                      />
                    </label>
                  </div>
                )}

                <button
                  onClick={saveChanges}
                  disabled={saving || !edit}
                  style={{
                    marginTop: 16, padding: "10px 18px",
                    background: "#2c3e50", color: "white",
                    border: "none", borderRadius: 7,
                    cursor: saving ? "wait" : "pointer"
                  }}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </button>

                <h4>Add New Note</h4>
                <select
                  value={noteType}
                  onChange={e => setNoteType(e.target.value)}
                  style={{ padding: 9, marginBottom: 10 }}
                >
                  {["General", "Call", "Visit"].map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
                <textarea
                  value={noteText}
                  onChange={e => setNoteText(e.target.value)}
                  placeholder="Enter a new note"
                  maxLength={5000}
                  rows={3}
                  style={{
                    display: "block", width: "100%",
                    boxSizing: "border-box", padding: 10,
                    border: "1px solid #cbd5e1", borderRadius: 7
                  }}
                />
                <button
                  onClick={addNote}
                  disabled={saving || !noteText.trim()}
                  style={{
                    marginTop: 10, padding: "10px 18px",
                    background: "#2563eb", color: "white",
                    border: "none", borderRadius: 7,
                    cursor: saving ? "wait" : "pointer"
                  }}
                >
                  Add Note
                </button>

                <h4>Notes</h4>
                {(selectedLead.notes || []).map((note, index) => (
                  <div key={note._id || index} style={{
                    borderBottom: "1px solid #e2e8f0", padding: "8px 0"
                  }}>
                    <strong>{note.type || "General"}</strong>
                    <p style={{ whiteSpace: "pre-wrap" }}>{note.text}</p>
                  </div>
                ))}

                <h4>WhatsApp History</h4>
                {(selectedLead.conversation || []).map((message, index) => (
                  <div key={index} style={{
                    borderBottom: "1px solid #e2e8f0", padding: "8px 0",
                    whiteSpace: "pre-wrap"
                  }}>
                    {message.message}
                  </div>
                ))}
                <p style={{ fontSize: 12, color: "#64748b" }}>
                  WhatsApp messaging is managed by the CRM owner.
                </p>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
